import React, { ReactNode, useState } from "react";
import HeimdallContext from "../HeimdallContext";
import { useUserData } from "../context/UserDataProvider";
import { Sidebar } from "./Sidebar";
import { Link, Outlet } from "react-router-dom";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import { AppBar, Box, Drawer, IconButton, Toolbar, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UserInfo from "./user_info/UserInfo";
import { FormattedMessage } from "react-intl";
import Color from "color";

const drawerWidth = 200;
const drawerBackgroundColor = "#3a3f51";

const Logo = () => {
    if (window.defaultTheme["logo-image"])
        return <img src={window.defaultTheme["logo-image"]} style={{ maxHeight: 64 }} />;

    return (
        <Typography variant="h4" noWrap color="white">
            <FormattedMessage id="app_name" />
        </Typography>
    );
};

type LoggedInPageProps = {
    context: HeimdallContext;
};

const OurDrawer = ({
    content,
    open,
    variant,
    onClose,
}: {
    content: ReactNode;
    open: boolean;
    variant: "desktop" | "mobile";
    onClose?: () => void;
}) => {
    const theme = useTheme();
    const textColor = theme.palette.getContrastText(drawerBackgroundColor);
    return (
        <Drawer
            variant={variant == "desktop" ? "permanent" : "temporary"}
            sx={{
                display: variant == "desktop" ? { xs: "none", sm: "block" } : "block",
                "& .MuiDrawer-paper": {
                    boxSizing: "border-box",
                    width: drawerWidth,
                    backgroundColor: drawerBackgroundColor,
                    borderRight: 0,
                    color: Color(textColor).darken(0.2).toString(),
                    // above app bar (needed for the mobile version)
                    zIndex: (theme) => theme.zIndex.appBar + 1,
                    "& .MuiListItemButton-root": {
                        fontWeight: "bold",
                    },
                    "& .Mui-selected": {
                        backgroundColor: "transparent",
                        color: textColor,
                    },
                    "& .MuiListItemButton-root:hover": {
                        color: textColor,
                        backgroundColor: Color(drawerBackgroundColor).lighten(0.2).toString(),
                    },
                },
            }}
            open={open}
            onClose={onClose}
            ModalProps={variant == "mobile" ? { keepMounted: true } : {}}
        >
            <Toolbar
                sx={{
                    justifyContent: "center",
                    backgroundColor: (theme) => theme.palette.primary.main,
                }}
            >
                <Link to="/" style={{ textDecoration: "none" }}>
                    <Logo />
                </Link>
            </Toolbar>
            <Box sx={{ marginBottom: 3 }} />
            {content}
        </Drawer>
    );
};

const LoggedInPage = ({ context }: LoggedInPageProps) => {
    const userData = useUserData();
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const drawerContent = <Sidebar context={context} userData={userData} />;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
            <AppBar
                position="fixed"
                color="default"
                sx={{
                    width: { xs: "100%", sm: `calc(100% - ${drawerWidth}px)` },
                    marginLeft: { xs: 0, sm: drawerWidth },
                    // zIndex: (theme) => theme.zIndex.appBar, // Default z-index for AppBar
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: "none" } }}
                    >
                        <FontAwesomeIcon icon={faBars} />
                    </IconButton>
                    <div style={{ flexGrow: 1 }} />
                    <UserInfo userInfo={userData} />
                </Toolbar>
            </AppBar>
            <OurDrawer
                content={drawerContent}
                open={mobileOpen}
                variant="mobile"
                onClose={() => setMobileOpen(false)}
            />
            <OurDrawer content={drawerContent} open variant="desktop" />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    padding: (theme) => theme.spacing(3),
                    marginLeft: { xs: 0, sm: `${drawerWidth}px` },
                }}
            >
                <Toolbar />
                <Outlet />
            </Box>
        </Box>
    );
};

export default LoggedInPage;
